<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb
          parentRouteName="BookingSelectList" 
          :extra-items="[
            {
              text: presetName,
              to: 'BookingList',
              params: $route.params.id
            },
            '店休設定'
          ]" 
        />
      </div>
    </div>

    <b-card>
      <div class="row d-flex mb-4 mb-xl-2 justify-content-between">
        <h4 class="col-12 col-xl-8 mb-2 font-weight-bold">{{ presetName }} 店休設定</h4>
      </div>
      <div class="d-flex mb-4 mb-xl-2 justify-content-between">
        <div class="col-3">
          <AppSingleSelect
            class=""
            v-model="branch"
            :options="branchOptions"
            @input="formatAndFetch"
          />
        </div>

        <div class="d-flex flex-column flex-md-row align-items-center">
          <b-button
            v-if="checkPermission([consts.BOOKING_BRANCH_SHIFT_CREATE])"
            class="flex-shrink-0 mb-2 mb-md-0 mr-md-2"
            variant="primary"
            :to="{ name: 'BookingBranchShiftCreate', query: { preset_id: preset_id, branch_id: query.branch_id }}"
            :disabled="!query.branch_id"
          >
            <i class="fa fa-plus"></i>新增
          </b-button>
          <b-form-select v-model="query.shift_type" :options="shiftTypeOptions" class="mr-2" @change="handlePageResetWithoutReload"/>
          <div class="d-flex align-items-stretch">
            <DateSearchPicker
              class="mr-1"
              v-model="query"
              startedPlaceholder='開始時間' endedPlaceholder="結束時間"
              @input="handleDateChange"
            />
            <b-button style="height: auto;" @click="handlePageResetWithoutReload">
              <i class="fa fa-search"></i>
            </b-button>
          </div>
        </div>
      </div>
      <b-card-text>
        <div class="row">
          <div class="col-12">
            <b-overlay :show="showLoading" rounded="sm">
              <b-table
                striped
                hover
                responsive
                :items="bookingShifts"
                :fields="fields"
              >
                <template #cell(actions)="data">
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.BOOKING_BRANCH_SHIFT_LIST_VIEW])"
                    variant="inverse-primary"
                    :to="{
                      name: 'BookingBranchShiftView',
                      params: { id: preset_id, shift_id: data.item.id },
                    }"
                  >
                    <span class="mdi mdi-eye"></span> 查看
                  </b-button>
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.BOOKING_BRANCH_SHIFT_MODIFY])"
                    variant="inverse-warning"
                    :to="{
                      name: 'BookingBranchShiftEdit',
                      params: { id: preset_id, shift_id: data.item.id },
                    }"
                  >
                    <span class="mdi mdi-lead-pencil"></span> 編輯
                  </b-button>
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.BOOKING_BRANCH_SHIFT_DELETE])"
                    variant="inverse-danger"
                    @click="deleteBranchShift(data.item)"
                  >
                    <span class="mdi mdi-delete"></span> 刪除
                  </b-button>
                </template>
              </b-table>
            </b-overlay>
          </div>
        </div>
        <CustomPagination
          :currentPage="query.page"
          :totalRows="totalRows"
          :perPage="query.per_page"
          @page-change="handlePageChange"
          @per-page-change="handlePerPageChange"
        />
      </b-card-text>
    </b-card>

    <FormUrlModal ref="formUrlModal" />
  </div>
</template>

<script>
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import { format } from "date-fns";
import bookingApi from "@/apis/booking";
import { mapGetters, mapState } from "vuex";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import FormUrlModal from "@/components/Dashboard/Booking/FormUrlModal.vue";
import { paginationMixin } from "@/mixins/pagination";
import { updateQueryFromRoute, updateUrlWithoutReload } from "@/utils/updateUrl";
import DateSearchPicker from "@/components/Page/Dashboard/DateSearchPicker.vue";
import moment from "moment/moment";
import AppSingleSelect from "@/components/AppSingleSelect.vue";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: {AppSingleSelect, DateSearchPicker, CustomPagination, FormUrlModal, BreadCrumb },
  mixins: [paginationMixin],
  data() {
    return {
      consts: consts,
      branch: {},
      preset_id: this.$route.params.id,
      bookingPreset: null,
      bookingShifts: [],
      shiftTypeOptions: [
        { text: '請選擇', value: null, },
        { text: '休假', value: 'leave' },
        { text: '上班', value: 'duty' },
      ],
      showLoading: false,
      fields: [
        {
          key: "shift_type",
          label: "類型",
          sortable: false,
          formatter: (value) => {
            return value === "leave" ? "休假" : "上班";
          },
        },
        {
          key: "start_at",
          label: "開始時間",
          sortable: true,
          formatter: (value) => {
            return format(new Date(value), "yyyy-MM-dd HH:mm");
          },
        },
        {
          key: "end_at",
          label: "結束時間",
          sortable: true,
          formatter: (value) => {
            return format(new Date(value), "yyyy-MM-dd HH:mm");
          },
        },
        { key: "actions", label: "管理" },
      ],
      totalRows: 0,
      initialized: false,
      query : {
        branch_id: null,
        shift_type: null,
        start_at: null,
        end_at: null,
        per_page: 20,
        page: 1,
      },
    };
  },
  computed: {
    ...mapGetters("route", ["routeQuery"]),
    ...mapGetters("general", ["myBranches", "currentBranch"]),
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}-${this.query.branch_id}`;
    },
    branchOptions() {
      return [{ text: '請選擇', value: null, }, ...this.myBranches.map(branch => {
        let branchName = `${branch.branch_code} ${branch.name}`
        return {
          value: branch.id,
          text: branchName
        }})]
    },
    presetName() {
      if (!this.bookingPreset) return null;
      return this.bookingPreset.name;
    },
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrlWithoutReload(this.query, this.$store, this.$route);
      },
    },
    routeQuery() {
      if (!this.initialized) return;
      this.fetchBranchShifts();
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
  },
  async mounted() {
    await this.fetchBookingPreset()
    if (!this.query.branch_id) {
      this.query.branch_id = this.currentBranch.id;
    }
    if (this.query.branch_id) {
      await this.fetchBranchShifts();

      this.branch = this.branchOptions.find(branch => branch.value === this.query.branch_id)
    }
    this.initialized = true;
  },
  methods: {
    formatAndFetch() {
      this.query.branch_id = this.branch.value;

      this.handlePageResetWithoutReload();
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    async fetchBookingPreset() {
      try {
        this.showLoading = true
        const response = await bookingApi.getBookingPreset(this.preset_id);
        this.bookingPreset = response.data.data
      } catch (error) {
        console.error(error)
        this.$swal("錯誤", "讀取資料集項目錯誤", "error")
      } finally {
        this.showLoading = false
      }
    },
    async fetchBranchShifts() {
      if (!this.query.branch_id) {
        return;
      }

      try {
        this.showLoading = true;
        let params = { ...this.query };
        let response = await bookingApi.getBookingShifts(this.preset_id, params);
        this.bookingShifts = response.data.data;
        this.totalRows = response.data.meta.total;
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取列表失敗", "error");
      } finally {
        this.showLoading = false;
      }
    },
    async deleteBranchShift(branchShift) {
      try {
        this.showLoading = true;
        const result = await this.$swal({
          type: "warning",
          title: "您確定要刪除嗎？",
          text: `是否要將此筆設定刪除？`,
          confirmButtonText: "確定刪除",
          cancelButtonText: "不刪除",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          reverseButtons: true,
        });

        if (!result.value) {
          return;
        }

        await bookingApi.deleteBookingShift(branchShift.id);
        this.$swal("刪除成功", "", "success");
        await this.fetchBranchShifts();
      } catch (error) {
        console.error(error);
        this.$swal("刪除失敗", "", "error");
      } finally {
        this.showLoading = false;
      }
    },
    handleDateChange(date) {
      const startAt = (date.start_at) ? moment(date.start_at).format("YYYY-MM-DD 00:00:00") : null;
      const endAt = (date.end_at) ? moment(date.end_at).format("YYYY-MM-DD 23:59:59") : null;

      this.query.start_at = startAt;
      this.query.end_at = endAt;
    },
  },
};
</script>

<style scoped>
.search-btn {
  height: 100%;
  display: flex;
  align-items: center;
}
</style>
